import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { logOut } from '../firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiUser, FiLogOut, FiChevronDown, FiMenu, FiX, FiShoppingBag,
  FiCalendar, FiGrid, FiUserPlus, FiImage, FiHome, FiMapPin, FiPhone, FiArrowRight
} from 'react-icons/fi';
import {
  GiKnifeFork, GiCupcake, GiPartyPopper,
  GiHotMeal, GiCook, GiPhotoCamera, GiPaperPlane
} from 'react-icons/gi';
import '../assets/css/styles.css';
import { ScrollContext } from '../contexts/ScrollContext';

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { currentUser, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { scrolled } = useContext(ScrollContext);

  useEffect(() => {
    setIsMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      const result = await logOut();
      if (result.success) {
        navigate('/');
      } else {
        console.error(result.message);
      }
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Animation variants
  const navbarVariants = {
    initial: { opacity: 0, y: -20 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 100, damping: 15 }
    }
  };

  const mobileMenuVariants = {
    closed: {
      opacity: 0,
      height: 0,
      transition: { duration: 0.3, ease: "easeInOut" }
    },
    open: {
      opacity: 1,
      height: "auto",
      transition: { duration: 0.3, ease: "easeInOut" }
    }
  };

  const userMenuVariants = {
    closed: {
      opacity: 0,
      scale: 0.95,
      y: -10,
      transition: { duration: 0.2, ease: "easeInOut" }
    },
    open: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.2, ease: "easeInOut" }
    }
  };

  return (
    <motion.nav
      initial="initial"
      animate="animate"
      variants={navbarVariants}
      className="fixed top-0 w-full z-50 transition-colors duration-300"
      style={{
        backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        boxShadow: scrolled ? '0 10px 40px rgba(0, 0, 0, 0.06)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(94, 15, 77, 0.08)' : 'none',
        height: '76px',
      }}
    >
      {scrolled &&
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary via-secondary to-accent opacity-90"></div>
      }

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
        <div className="flex justify-between items-center h-full">
          {/* Logo and site name */}
          <div className="flex items-center">
            <motion.div
              className="flex-shrink-0 flex items-center"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <Link to="/" className="flex items-center gap-3">
                <div className="h-14 w-14 overflow-hidden flex items-center justify-center rounded-xl bg-black shadow-lg relative">
                  <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_white_0%,_transparent_60%)]"></div>
                  <img src="/icon.png" alt="Happy Bubble Logo" className="w-15 h-15 object-contain relative z-10" />
                </div>
                <div>
                  <h1 className="m-0 p-0 font-display text-xl leading-none text-black"
                    style={{ textShadow: '1px 1px 0px rgba(44, 43, 2, 0.1)' }}>
                    Happy Bubble
                  </h1>
                  <p className="text-xs text-primary-light font-medium tracking-wider">PRIVATE THEATERS</p>
                </div>
              </Link>
            </motion.div>

            {/* Desktop Navigation Links */}
            <div className="hidden md:ml-12 md:flex md:space-x-1">
              <NavLink to="/" icon={<FiHome />} label="Home" isActive={isActive('/')} />
              <NavLink to="/branches" icon={<GiCook />} label="Book Now" isActive={isActive('/branches')} />
              <NavLink to="/about" icon={<FiUser />} label="About" isActive={isActive('/about')} />
              <NavLink to="/gallery" icon={<GiPhotoCamera />} label="Gallery" isActive={isActive('/gallery')} />
              <NavLink to="/contact" icon={<FiPhone />} label="Contact" isActive={isActive('/contact')} />

              {currentUser && (
                <NavLink to="/dashboard" icon={<FiGrid />} label="Dashboard" isActive={isActive('/dashboard')} />
              )}

              {isAdmin && (
                <NavLink to="/admin" icon={<GiCupcake />} label="Admin" isActive={isActive('/admin')} isAdmin={true} />
              )}
            </div>
          </div>

          {/* Right side - User options */}
          <div className="hidden md:ml-6 md:flex md:items-center">
            {currentUser ? (
              <div className="relative flex items-center">
                <motion.button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-3 rounded-full px-5 py-2 transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    backgroundColor: userMenuOpen ? 'rgba(94, 15, 77, 0.06)' : 'transparent',
                    border: '2px solid var(--primary)',
                  }}
                >
                  <div className="h-9 w-9 rounded-full overflow-hidden relative flex items-center justify-center shadow-inner">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-secondary"></div>
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_white_0%,_transparent_60%)] opacity-30"></div>
                    <span className="relative z-10 text-white font-semibold">
                      {currentUser.displayName ? currentUser.displayName.charAt(0).toUpperCase() : 'U'}
                    </span>
                  </div>
                  <div>
                    {/* <span className="font-medium text-sm block leading-tight" style={{ color: 'var(--text-primary)' }}>
                      {currentUser.displayName || currentUser.email.split('@')[0]}
                    </span> */}
                    <span className="text-xs text-gray-500">Account</span>
                  </div>
                  <FiChevronDown
                    size={16}
                    style={{
                      transform: userMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      color: 'var(--primary)',
                      transition: 'transform 0.2s ease'
                    }}
                  />
                </motion.button>

                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial="closed"
                      animate="open"
                      exit="closed"
                      variants={userMenuVariants}
                      className="absolute right-0 top-full mt-2 w-60 rounded-xl py-2 z-50"
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid rgba(94, 15, 77, 0.08)',
                        boxShadow: '0 15px 50px rgba(0, 0, 0, 0.12)',
                        borderRadius: 'var(--radius-md)'
                      }}
                    >
                      <div className="px-5 py-3 border-b border-gray-100">
                        <p className="text-xs font-semibold text-secondary mb-1">SIGNED IN AS</p>
                        <p className="text-sm font-medium truncate text-gray-700">
                          {currentUser.email}
                        </p>
                      </div>

                      <div className="py-2">
                        <Link
                          to="/dashboard"
                          className="block px-5 py-2.5 text-sm transition-colors duration-200 flex items-center gap-3 hover:bg-gray-50"
                          style={{ color: 'var(--text-secondary)' }}
                        >
                          <div className="h-8 w-8 rounded-lg bg-primary/5 flex items-center justify-center text-primary">
                            <FiGrid size={15} />
                          </div>
                          <span>My Dashboard</span>
                        </Link>

                        {isAdmin && (
                          <Link
                            to="/admin"
                            className="block px-5 py-2.5 text-sm transition-colors duration-200 flex items-center gap-3 hover:bg-gray-50"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            <div className="h-8 w-8 rounded-lg bg-secondary/10 flex items-center justify-center text-secondary">
                              <GiCupcake size={15} />
                            </div>
                            <span>Admin Dashboard</span>
                          </Link>
                        )}
                      </div>

                      <div className="border-t border-gray-100 mt-1 pt-1">
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-5 py-2.5 text-sm transition-colors duration-200 flex items-center gap-3 hover:bg-gray-50 text-primary-dark hover:text-primary"
                        >
                          <div className="h-8 w-8 rounded-lg bg-primary/5 flex items-center justify-center text-primary">
                            <FiLogOut size={15} />
                          </div>
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Link
                    to="/login"
                    className="text-sm font-medium px-5 py-2.5 rounded-full border-2 border-primary text-secondary-dark hover:bg-primary/5 transition-all duration-300 flex items-center gap-2"
                  >
                    <span>Sign In</span>
                  </Link>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Link
                    to="/signup"
                    className="group text-sm font-medium px-5 py-2.5 rounded-full bg-primary text-secondary-dark hover:bg-primary-dark transition-all duration-300 shadow-md hover:shadow-lg flex items-center gap-2"
                  >
                    <span>Sign Up</span>
                    <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </motion.div>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <motion.button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-full text-primary hover:text-primary-dark hover:bg-primary-100 focus:outline-none transition duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isMenuOpen ? (
                <FiX className="h-6 w-6" />
              ) : (
                <FiMenu className="h-6 w-6" />
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial="closed"
            animate="open"
            exit="closed"
            variants={mobileMenuVariants}
            className="md:hidden overflow-hidden"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              borderTop: '1px solid var(--border)',
              boxShadow: '0 15px 35px rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(15px)',
              WebkitBackdropFilter: 'blur(15px)',
            }}
          >
            <div className="px-4 pt-2 pb-4 space-y-1 sm:px-6">
              <MobileNavLink to="/" icon={<FiHome />} label="Home" isActive={isActive('/')} />
              <MobileNavLink to="/branches" icon={<GiCook />} label="Book Now" isActive={isActive('/branches')} />
              <MobileNavLink to="/about" icon={<FiUser />} label="About" isActive={isActive('/about')} />
              <MobileNavLink to="/gallery" icon={<GiPhotoCamera />} label="Gallery" isActive={isActive('/gallery')} />
              <MobileNavLink to="/contact" icon={<FiPhone />} label="Contact" isActive={isActive('/contact')} />

              {currentUser ? (
                <>
                  <MobileNavLink to="/dashboard" icon={<FiGrid />} label="Dashboard" isActive={isActive('/dashboard')} />
                  {isAdmin && (
                    <MobileNavLink to="/admin" icon={<GiCupcake />} label="Admin" isActive={isActive('/admin')} isAdmin={true} />
                  )}
                  <div className="border-t border-gray-200 my-4"></div>
                  <div
                    onClick={handleLogout}
                    className="flex items-center px-3 py-3 text-base font-medium text-primary-dark hover:text-primary hover:bg-gray-50 rounded-lg transition duration-200 gap-3"
                  >
                    <div className="h-8 w-8 rounded-lg bg-primary/5 flex items-center justify-center text-primary">
                      <FiLogOut size={16} />
                    </div>
                    <span>Sign Out</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="border-t border-gray-200 my-4"></div>
                  <div className="flex flex-col gap-3 px-3 py-2">
                    <Link
                      to="/login"
                      className="flex-1 text-center text-sm font-medium py-3 rounded-lg border-2 border-primary text-secondary-dark hover:bg-primary/5 transition-colors duration-200 flex items-center justify-center"
                    >
                      <FiUser className="mr-2" size={16} />
                      <span>Sign In</span>
                    </Link>
                    <Link
                      to="/signup"
                      className="flex-1 text-center text-sm font-medium py-3 rounded-lg bg-primary text-white hover:bg-primary-dark transition-colors duration-200 flex items-center justify-center"
                    >
                      <FiUserPlus className="mr-2" size={16} />
                      <span>Create Account</span>
                    </Link>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}

function NavLink({ to, icon, label, isActive, isAdmin }) {
  return (
    <Link
      to={to}
      className={`px-4 py-2.5 font-medium rounded-xl transition-all duration-200 flex items-center space-x-2 ${isActive
          ? 'bg-gradient-to-br from-primary to-primary/5 text-dark shadow-sm'
          : 'text-gray-700 hover:text-primary hover:bg-primary/5'
        } ${isAdmin ? 'bg-amber-50/80 text-amber-800' : ''}`}
    >
      <span className={`${isActive ? 'text-dark' : 'text-gray-500'}`}>
        {React.cloneElement(icon, { size: 16 })}
      </span>
      <span>{label}</span>
      {isActive && (
        <motion.span
          className="inline-block w-1 h-1 rounded-full bg-primary ml-1"
          animate={{ scale: [1, 1.3, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        />
      )}
    </Link>
  );
}

function MobileNavLink({ to, icon, label, isActive, isAdmin }) {
  return (
    <Link
      to={to}
      className={`flex items-center px-3 py-3 font-medium rounded-xl transition duration-200 ${isActive
          ? 'bg-gradient-to-br from-primary/10 to-primary/5 text-primary'
          : 'text-gray-700 hover:text-primary hover:bg-primary/5'
        } ${isAdmin ? 'bg-amber-50 text-amber-800' : ''}`}
    >
      <div className={`mr-3 h-8 w-8 rounded-lg flex items-center justify-center ${isActive ? 'bg-white/80 text-primary' : 'bg-gray-100/80 text-gray-500'}`}>
        {React.cloneElement(icon, { size: 18 })}
      </div>
      <span>{label}</span>
      {isActive && (
        <motion.span
          className="ml-auto mr-1 w-1.5 h-1.5 rounded-full bg-primary"
          animate={{ scale: [1, 1.3, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        />
      )}
    </Link>
  );
}